import { Box } from '@mui/material';
import { ScatterChartProps } from '@mui/x-charts';
import { ScatterItemIdentifier } from '@mui/x-charts/models';
import React, { useMemo } from 'react';

import ScatterChart from 'components/charts/scatter-chart';
import ChartLegend from 'components/charts/chart-legend';
import { ITEM_HIGHLIGHT } from 'components/charts/constants/chart-defaults';
import { useChartLegendVisibility } from 'components/charts/hooks/use-chart-legend-visibility';
import { useChartAnimation } from 'components/charts/hooks/use-chart-animation';
import { useSeriesVisibility } from 'components/charts/hooks/use-series-visibility';
import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import DataLabChartHiddenSeriesOverlay from 'views/playground/features/data-lab/components/data-lab-chart-hidden-series-overlay';
import DataLabChartLegendVisibilityMenu from 'views/playground/features/data-lab/components/data-lab-chart-legend-visibility-menu';
import DataLabChartTooltip from 'views/playground/features/data-lab/components/data-lab-chart-tooltip';
import { SelectableChartProps } from 'views/playground/features/data-lab/types/selectable-chart.type';
import { ScatterSeriesData } from 'views/playground/features/data-lab/types/chart-series.type';
import { useMetricFormatters } from 'views/playground/features/data-lab/hooks/use-metric-formatters';

type Props = SelectableChartProps &
  Pick<ScatterChartProps, 'height'> & {
    data: ScatterSeriesData[];
  };

const CorrelationScatterChart: React.FC<Props> = ({ data, height = 320, selectedId, onSelect }) => {
  const { formatRatio, formatWeightG } = useMetricFormatters();
  const palette = useChartPalette();
  const chartSkipAnimation = useChartAnimation();
  const { visible: legendVisible, toggle: toggleLegend } = useChartLegendVisibility();

  const coloredSeries = useMemo(
    () =>
      data.map((series, index) => ({
        ...series,
        color: palette.series.categorical[index % palette.series.categorical.length],
      })),
    [data, palette.series.categorical]
  );

  const { hiddenIds, toggle, visibleSeries } = useSeriesVisibility(coloredSeries);
  // The card cannot see per-series visibility, so this chart owns the
  // all-series-hidden message.
  const allSeriesHidden = coloredSeries.length > 0 && visibleSeries.length === 0;

  // Keep both axes stable when legend filtering hides every series.
  const { xMin, xMax, yMin, yMax } = useMemo(() => {
    const points = coloredSeries.flatMap((series) => series.points);
    if (points.length === 0) return { xMin: undefined, xMax: undefined, yMin: undefined, yMax: undefined };
    const xValues = points.map((point) => point.x);
    const yValues = points.map((point) => point.y);
    return { xMin: Math.min(...xValues), xMax: Math.max(...xValues), yMin: Math.min(...yValues), yMax: Math.max(...yValues) };
  }, [coloredSeries]);

  return (
    <>
      <Box display="flex" justifyContent="flex-end">
        <DataLabChartLegendVisibilityMenu visible={legendVisible} onToggle={toggleLegend} />
      </Box>
      <ScatterChart
        height={height}
        skipAnimation={chartSkipAnimation}
        hideLegend
        // Zoom and pan require an unlicensed MUI X Pro chart.
        xAxis={[{ label: 'Average weight (g)', min: xMin, max: xMax }]}
        yAxis={[{ label: 'Cumulative FCR', min: yMin, max: yMax }]}
        series={visibleSeries.map((series) => ({
          id: series.id,
          label: series.label,
          color: series.color,
          data: series.points.map((point) => ({ id: point.id, x: point.x, y: point.y })),
          valueFormatter: (value) => (value ? `${formatWeightG(value.x)} · ${formatRatio(value.y)} FCR` : ''),
          ...ITEM_HIGHLIGHT,
        }))}
        // Leave item highlighting uncontrolled. The legend hook tracks only a
        // series ID, while this chart's item highlight also needs dataIndex.
        onItemClick={(_event: React.MouseEvent, scatterItemIdentifier: ScatterItemIdentifier) => {
          const series = data.find((candidate) => candidate.id === scatterItemIdentifier.seriesId);
          const point = series?.points[scatterItemIdentifier.dataIndex];
          if (!point) return;
          onSelect?.(point.id === selectedId ? null : point.id);
        }}
        slots={{ noDataOverlay: DataLabChartHiddenSeriesOverlay, tooltip: DataLabChartTooltip }}
        sx={
          allSeriesHidden
            ? // Dims axis ticks/labels so the frame reads as inert rather than
              // real data; state-dependent, so it cannot live in the theme.
              { '& text:not(.chart-hidden-series-overlay-text)': { opacity: 0.4 } }
            : undefined
        }
      />
      {legendVisible && (
        <ChartLegend
          items={coloredSeries.map((series) => ({ id: String(series.id), label: series.label, color: series.color }))}
          hiddenIds={hiddenIds}
          onToggle={toggle}
        />
      )}
    </>
  );
};

export default CorrelationScatterChart;
